import {distinctUntilChanged, map, mergeMap, share} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {getMessageFromException} from '../utilities/Util';
import {
  Company,
  CompanyClient,
  KcApplication,
  KcApplicationClient,
  KcApplicationResult,
  KcNote, KcNoteResult,
  KcProgramLevel, KcStatus,
  PagedListOfKcApplication
} from './api.client';
import {NotificationService} from './NotificationService';
import {SystemService} from './SystemService';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {StateResetRegistry} from './state-reset-registry.service';

// 003-retire-ngrx (US2): the kcApp slice moved off NgRx into this service.
// Selection/result/lookups genuinely span the enrollment wizard (steps 1-3),
// its resolver guards, and the client application pages, so they stay shared.
// Not ported (written but never read — no selectors existed): currentKcNotes,
// currentClientApplications, kcPledge; also dropped the dead
// clearCurrentSelectedKcApplicationResult action, which no reducer handled
// (dispatching it was a no-op) and no component called. The paged search
// results are NOT held here: their only consumer is ApplicationListComponent,
// whose search criteria live on the component — service-held results outlived
// the criteria that produced them, breaking pagination after back-navigation.
interface KcApplicationSliceState {
  currentSelectedKcApplication: KcApplication;
  currentKcApplicationResult: KcApplicationResult;
  programDetail: KcProgramLevel;
  kcAmerenCompany: Company;
}

const initialState: KcApplicationSliceState = {
  currentSelectedKcApplication: null,
  currentKcApplicationResult: null,
  programDetail: null,
  kcAmerenCompany: null
};

@Injectable()
export class KcApplicationService {

  private readonly _state$ = new BehaviorSubject<KcApplicationSliceState>(initialState);

  // ---------------- Streams (former kcApp selectors) ----------------
  readonly programDetail$ = this.select((s) => s.programDetail);
  readonly currentSelectedKcApplication$ = this.select((s) => s.currentSelectedKcApplication);
  readonly kcAmerenCompany$ = this.select((s) => s.kcAmerenCompany);
  readonly currentKcApplicationResult$ = this.select((s) => s.currentKcApplicationResult);

  constructor(public _kcApplicationApiService: KcApplicationClient,
              public _notificationService: NotificationService,
              public _companyApiService: CompanyClient,
              public systemService: SystemService,
              resetRegistry: StateResetRegistry) {
    resetRegistry.register(() => this._state$.next(initialState));
  }

  getApplications(searchValueAccount: string,
                  searchValueSSN: string,
                  agencyId?: string,
                  statusId?: string,
                  sortName?: string,
                  descending?: boolean,
                  limit?: number,
                  offset?: number): Observable<PagedListOfKcApplication> {
    const request = this._kcApplicationApiService.retrieveKcApplicationsPaged(
      searchValueAccount,
      searchValueSSN,
      agencyId,
      statusId,
      limit,
      offset,
      sortName,
      null,
      descending
    ).pipe(share());
    request.subscribe({ next: () => {
    }, error: () => {
      this._notificationService.showError('Error Loading Applications');
    }, complete: () => {
    }});
    return request;
  }

  addKcApplication(kcApplication: KcApplication): Observable<KcApplication> {
    const request = this._kcApplicationApiService.addKcApplication(kcApplication).pipe(share());
    request.subscribe({ next: (result) => {
      this.setCurrentSelectedKcApplicationAction(result);
      this._notificationService.showSuccess('Keeping Current Application Successfully Created!');
    }, error: (err) => {
        this._notificationService.showError(getMessageFromException(err));
    }, complete: () => {
    }});
    return request;
  }

  updateKcApplication(kcApplication: KcApplication): Observable<KcApplication> {
    const request = this._kcApplicationApiService.updateKcApplication(kcApplication).pipe(share());
    request.subscribe({ next: (result) => {
      this.setCurrentSelectedKcApplicationAction(result);
      this._notificationService.showSuccess('Keeping Current Application Successfully Updated!');
    }, error: (err) => {
        this._notificationService.showError(getMessageFromException(err));
    }, complete: () => {
    }});
    return request;
  }

  createApplicationEnrollment(application: KcApplication): Observable<KcApplication> {
    const request = this._kcApplicationApiService.createApplicationEnrollment(application).pipe(share());
    request.subscribe({ next: () => {
      this.setCurrentKcApplicationResult(null);
      this._notificationService.showSuccess('Created Application Enrollment - Status Pending for Approval');
    }, error: (err) => {
        this._notificationService.showError(getMessageFromException(err));
    }, complete: () => {
    }});
    return request;
  }

  deleteKcApplication(application: KcApplication): any {
    const request = this.systemService
      .kcStatusDelete$
        .pipe(
          mergeMap((kcStatus: KcStatus) => {
            const kcApplication = KcApplication.fromJS({ ...application, keepingCurrentStatusId: kcStatus.id });
            return this._kcApplicationApiService.updateKcApplication(kcApplication);
          }))
      .pipe(map(() => application), share());
    request.subscribe(() => {
      this.setCurrentKcApplicationResult(null);
      this._notificationService.showSuccess('Successfully Deleted Application');
    }, (err) => {
        this._notificationService.showError(getMessageFromException(err));
    }, () => {
    });
    return request;
  }

  // updateKcApplication (kcApplication: KcApplication) {
  //     let request = this._kcApplicationApiService.updateKcApplication(kcApplication).share()
  //     request.subscribe((result) => {
  //         this.setCurrentSelectedKcApplication(result);
  //         this._notificationService.showSuccess("Keeping Current Application Succesfully Updated!");
  //     }, (err) => {
  //         this._notificationService.showError("There was a problem updating this Keeping Current Application.");
  //     }, () => {
  //
  //     })
  //     return request;
  // }
  getKcApplicationsByClientId(id: string): Observable<KcApplication[]> {
    // 003-retire-ngrx: the old store write (setCurrentClientsApplications) was
    // never read back — callers consume the returned observable directly.
    const request = this._kcApplicationApiService.retrieveKcApplicationsByClientId(id).pipe(share());
    request.subscribe({ next: () => {
    }, error: () => {
      this._notificationService.showError(
        'There was a problem retrieving this client\'s Keeping Current Applications.');
    }, complete: () => {
    }});
    return request;
  }

  // getIncompleteOrApprovedApplicationByClientId (clientId: string) {
  //     let request = this._kcApplicationApiService.getIncompleteOrApprovedApplication(clientId).share();
  //     request.subscribe((result) => {
  //
  //     }, (err) => {
  //         this._notificationService.showError("There was a problem retrieving this client's Keeping Current Application.");
  //     }, () => {
  //
  //     });
  //     return request;
  // }
  getIncompleteOrApprovedApplicationByClientId(clientId: string): Observable<KcApplication> {
    const request = this._kcApplicationApiService.getIncompleteOrApprovedApplication(clientId).pipe(
      // base api apparently returns an object on a 204 status.
      // fixing this issue globally might break other code.
      // In this case we need to differentiate between 204 and 200 status
      map((obj) => (obj && obj.id) ? obj : null),
      share(), );
    request.subscribe({ next: (result) => {
      this.setCurrentSelectedKcApplicationAction(result);
    }, error: () => {
      this._notificationService.showError(
        'There was a problem retrieving the selected Keeping Current Application.');
    }, complete: () => {
    }});
    return request;
  }

  getKcApplicationById(id: string): Observable<KcApplicationResult> {
    const request = this._kcApplicationApiService.retrieveKcApplication(id).pipe(share());
    request.subscribe({ next: (result) => {
      this.setCurrentSelectedKcApplicationAction(result);
      this.setCurrentKcApplicationResult(result);
    }, error: () => {
      this._notificationService.showError(
        'There was a problem retrieving the selected Keeping Current Application.');
    }, complete: () => {
    }});
    return request;
  }

  getKcNotes(applicationId: string): Observable<KcNoteResult[]> {
    const request = this._kcApplicationApiService.getKeepingCurrentNotes(applicationId).pipe(share());
    request.subscribe({ next: () => {
    }, error: () => {
    }, complete: () => {
    }});
    return request;
  }

  addKcNote(note: KcNote): Observable<KcNote> {
    const request = this._kcApplicationApiService.addKcNote(note).pipe(share());
    request.subscribe({ next: () => {
      this._notificationService.showSuccess('Successfully added a note!');
    }, error: () => {
    }, complete: () => {
    }});
    return request;
  }

  deleteKcNote(id: string): Observable<void> {
    const request = this._kcApplicationApiService.deleteNoteById(id).pipe(share());
    request.subscribe({ next: () => {
      this._notificationService.showSuccess('Successfully deleted a note!');
    }, error: () => {
    }, complete: () => {
    }});
    return request;
  }

  getProgramDetails(programId: string, federalPovertyLevel: number): Observable<KcProgramLevel> {
    const request = this._kcApplicationApiService.getProgramDetails(programId, federalPovertyLevel).pipe(share());
    request.subscribe({ next: (result) => {
      this.setProgramDetail(result);
    }, error: (err) => {
      if (typeof err === 'string') {
        this._notificationService.showError(err);
      } else {
        this._notificationService.showError('There was an error getting the relevant Program Details');
      }
    }, complete: () => {
    }});
    return request;
  }

  setProgramDetail(programDetail: KcProgramLevel): void {
    this.patch({ programDetail });
  }

  getKcAmerenCompany(): Observable<Company> {
    // TODO: use Ameren GUID
    const request = this._companyApiService.retrieveCompaniesPaged('Ameren Missouri', null).pipe(
      map((result) => result.items[0]),
      share(), );
    request.subscribe({ next: (company) => {
      this.setKcAmerenCompany(company);
    }, error: () => {
    }, complete: () => {
    }});
    return request;
  }

  setKcAmerenCompany(company: Company): void {
    this.patch({ kcAmerenCompany: company });
  }

  setCurrentKcApplicationResult(kcApplicationResult: KcApplicationResult): void {
    this.patch({ currentKcApplicationResult: kcApplicationResult });
  }

  setCurrentSelectedKcApplicationAction(selectedKcApplication: KcApplication): void {
    this.patch({ currentSelectedKcApplication: selectedKcApplication });
  }

  // ---------------- State plumbing ----------------
  private select<T>(project: (state: KcApplicationSliceState) => T): Observable<T> {
    return this._state$.pipe(map(project), distinctUntilChanged());
  }

  private patch(partial: Partial<KcApplicationSliceState>): void {
    this._state$.next({ ...this._state$.value, ...partial });
  }
}
